import React, { useState, useCallback, useEffect } from "react";
import { motion } from "framer-motion";
import { io } from "socket.io-client";
import {
  Server,
  Cloud,
  Bell,
  RefreshCw,
  CheckCircle2,
  XCircle,
  Loader2,
  Radio,
  Send,
} from "lucide-react";
import PageWrapper from "../../components/layout/PageWrapper";
import Button from "../../components/forms/Button";
import { axiosPrivate } from "../../api/axios";
import { testSupabaseConnection } from "../../config/supabase";
import Toast from "../../components/ui/Toast";
import { getSocketUrl, getSocketOptions } from "../../utils/socketConfig";

const SOCKET_CHECK_TIMEOUT_MS = 8000;

const StatusCard = ({
  icon: Icon,
  title,
  subtitle,
  status,
  loading,
  onCheck,
  buttonLabel,
  extra,
}) => (
  <motion.div
    initial={{ opacity: 0, y: 16 }}
    animate={{ opacity: 1, y: 0 }}
    className="card flex flex-col"
  >
    <div className="flex items-start justify-between gap-4">
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-xl bg-gray-100 dark:bg-dark-300 flex items-center justify-center">
          <Icon className="w-6 h-6 text-primary-accent" />
        </div>
        <div>
          <h3 className="font-semibold text-gray-900 dark:text-light-100">
            {title}
          </h3>
          <p className="text-sm text-gray-500 dark:text-light-500">
            {subtitle}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {loading && <Loader2 className="w-5 h-5 animate-spin text-gray-400" />}
        {!loading &&
          status !== null &&
          status !== undefined &&
          (status.ok ? (
            <CheckCircle2 className="w-6 h-6 text-green-500" />
          ) : (
            <XCircle className="w-6 h-6 text-red-500" />
          ))}
      </div>
    </div>
    {extra}
    {onCheck && (
      <Button
        variant="ghost"
        size="sm"
        onClick={onCheck}
        disabled={loading}
        className="mt-4 self-start"
      >
        {loading ? "Checking…" : buttonLabel || "Check"}
      </Button>
    )}
  </motion.div>
);

const SystemStatus = () => {
  const [backendStatus, setBackendStatus] = useState(null);
  const [backendLoading, setBackendLoading] = useState(false);
  const [supabaseStatus, setSupabaseStatus] = useState(null);
  const [supabaseLoading, setSupabaseLoading] = useState(false);
  const [socketStatus, setSocketStatus] = useState(null);
  const [socketLoading, setSocketLoading] = useState(false);
  const [firebaseLoading, setFirebaseLoading] = useState(false);
  const [firebaseResult, setFirebaseResult] = useState(null);
  const [broadcastTitle, setBroadcastTitle] = useState("");
  const [broadcastBody, setBroadcastBody] = useState("");
  const [broadcastTarget, setBroadcastTarget] = useState("all"); // "all" | "selected"
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [employeesList, setEmployeesList] = useState([]);
  const [employeesLoading, setEmployeesLoading] = useState(false);
  const [broadcastLoading, setBroadcastLoading] = useState(false);
  const [broadcastResult, setBroadcastResult] = useState(null);
  const [toast, setToast] = useState({
    isVisible: false,
    message: "",
    type: "success",
  });

  useEffect(() => {
    let cancelled = false;
    const fetchEmployees = async () => {
      setEmployeesLoading(true);
      try {
        const res = await axiosPrivate.get("/api/employees");
        const list = Array.isArray(res.data)
          ? res.data
          : (res.data?.data ?? []);
        if (!cancelled) setEmployeesList(list);
      } catch {
        if (!cancelled) setEmployeesList([]);
      } finally {
        if (!cancelled) setEmployeesLoading(false);
      }
    };
    fetchEmployees();
    return () => {
      cancelled = true;
    };
  }, []);

  const toggleUserSelection = (userId) => {
    const id = String(userId);
    setSelectedUserIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const selectAllUsers = () => {
    const ids = employeesList
      .map((e) => e._id?.toString() || e.id)
      .filter(Boolean);
    setSelectedUserIds(ids);
  };

  const clearSelection = () => setSelectedUserIds([]);

  const showToast = (message, type = "success") => {
    setToast({ isVisible: true, message, type });
  };
  const hideToast = () =>
    setToast({ isVisible: false, message: "", type: "success" });

  const checkBackend = useCallback(async () => {
    setBackendLoading(true);
    setBackendStatus(null);
    try {
      const res = await axiosPrivate.get("/api/system/status");
      const data = res.data || {};
      setBackendStatus({
        ok: data.backend === true && data.database === "connected",
        backend: data.backend,
        database: data.database,
      });
    } catch (err) {
      setBackendStatus({
        ok: false,
        backend: false,
        database: "unknown",
        error: err.response?.data?.message || err.message || "Request failed",
      });
    } finally {
      setBackendLoading(false);
    }
  }, []);

  const checkSupabase = useCallback(async () => {
    setSupabaseLoading(true);
    setSupabaseStatus(null);
    const result = await testSupabaseConnection();
    setSupabaseStatus(result);
    setSupabaseLoading(false);
  }, []);

  const checkSocket = useCallback(() => {
    const token = localStorage.getItem("accessToken");
    if (!token) {
      setSocketStatus({
        ok: false,
        message: "No auth token. Log in and try again.",
      });
      return;
    }

    setSocketLoading(true);
    setSocketStatus(null);

    const socket = io(getSocketUrl(), getSocketOptions(token));

    const timeoutId = setTimeout(() => {
      if (socket.connected) return;
      socket.close();
      setSocketStatus({
        ok: false,
        message: "Connection timed out. Socket server may be unreachable.",
      });
      setSocketLoading(false);
    }, SOCKET_CHECK_TIMEOUT_MS);

    socket.on("connect", () => {
      clearTimeout(timeoutId);
      setSocketStatus({
        ok: true,
        message: "Socket.IO connected successfully.",
      });
      socket.disconnect();
      setSocketLoading(false);
    });

    socket.on("connect_error", (err) => {
      clearTimeout(timeoutId);
      setSocketStatus({
        ok: false,
        message:
          err?.message || "Connection failed (auth or server unreachable).",
      });
      setSocketLoading(false);
    });
  }, []);

  const [discordStatus, setDiscordStatus] = useState(null);
  const [discordLoading, setDiscordLoading] = useState(false);
  // Not part of "Refresh all": it posts a real message to the channel.
  const sendDiscordTest = useCallback(async () => {
    setDiscordLoading(true);
    setDiscordStatus(null);
    try {
      const res = await axiosPrivate.post("/api/system/discord-test");
      setDiscordStatus({
        ok: res.data?.ok === true,
        message: res.data?.message || "Test sent.",
        results: res.data?.results || [],
      });
    } catch (err) {
      setDiscordStatus({
        ok: false,
        message: err.response?.data?.message || err.message || "Could not reach the server",
        results: [],
      });
    } finally {
      setDiscordLoading(false);
    }
  }, []);

  const sendTestNotification = useCallback(async () => {
    setFirebaseLoading(true);
    setFirebaseResult(null);
    try {
      const res = await axiosPrivate.post("/api/notifications/test");
      const data = res.data || {};
      setFirebaseResult({
        ok: data.success === true,
        message: data.message || "Notification sent.",
      });
      showToast(data.message || "Test notification sent.", "success");
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        err.message ||
        "Failed to send test notification";
      setFirebaseResult({ ok: false, message: msg });
      showToast(msg, "error");
    } finally {
      setFirebaseLoading(false);
    }
  }, []);

  const sendBroadcastNotification = useCallback(async () => {
    const body = (broadcastBody || "").trim();
    if (!body) {
      showToast("Please enter a message.", "error");
      return;
    }
    if (broadcastTarget === "selected" && selectedUserIds.length === 0) {
      showToast("Select at least one user, or choose 'All users'.", "error");
      return;
    }
    setBroadcastLoading(true);
    setBroadcastResult(null);
    try {
      const payload = {
        title: (broadcastTitle || "").trim() || undefined,
        body,
      };
      if (broadcastTarget === "selected" && selectedUserIds.length > 0) {
        payload.userIds = selectedUserIds;
      }
      const res = await axiosPrivate.post(
        "/api/notifications/broadcast",
        payload,
      );
      const data = res.data || {};
      setBroadcastResult({
        ok: data.success === true,
        message: data.message || "Sent.",
        totalUsers: data.totalUsers,
      });
      showToast(data.message || "Notification sent.", "success");
      setBroadcastTitle("");
      setBroadcastBody("");
      setSelectedUserIds([]);
    } catch (err) {
      const msg =
        err.response?.data?.message || err.message || "Failed to send";
      setBroadcastResult({ ok: false, message: msg });
      showToast(msg, "error");
    } finally {
      setBroadcastLoading(false);
    }
  }, [broadcastTitle, broadcastBody, broadcastTarget, selectedUserIds]);

  const runAllChecks = useCallback(() => {
    checkBackend();
    checkSupabase();
    checkSocket();
    setFirebaseResult(null);
  }, [checkBackend, checkSupabase, checkSocket]);

  return (
    <PageWrapper
      title="System Status"
      subtitle="Verify that core services are operational (High Potit only)"
      actions={
        <Button
          variant="ghost"
          onClick={runAllChecks}
          className="flex items-center gap-2"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh all
        </Button>
      }
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Backend + Database */}
        <StatusCard
          icon={Server}
          title="Backend server"
          subtitle="API and database connection"
          status={backendStatus ? { ok: backendStatus.ok } : null}
          loading={backendLoading}
          onCheck={checkBackend}
          buttonLabel="Check backend"
          extra={
            backendStatus &&
            !backendLoading && (
              <div className="mt-3 text-sm text-gray-600 dark:text-light-400">
                <p>
                  Backend: {backendStatus.backend ? "Running" : "Unreachable"}
                </p>
                <p>Database: {backendStatus.database ?? "—"}</p>
                {backendStatus.error && (
                  <p className="text-red-600 dark:text-red-400">
                    {backendStatus.error}
                  </p>
                )}
              </div>
            )
          }
        />

        {/* Supabase */}
        <StatusCard
          icon={Cloud}
          title="Supabase"
          subtitle="Storage and services"
          status={supabaseStatus}
          loading={supabaseLoading}
          onCheck={checkSupabase}
          buttonLabel="Check Supabase"
          extra={
            supabaseStatus &&
            !supabaseLoading && (
              <p className="mt-3 text-sm text-gray-600 dark:text-light-400">
                {supabaseStatus.message}
              </p>
            )
          }
        />

        {/* Socket.IO */}
        <StatusCard
          icon={Radio}
          title="Socket.IO"
          subtitle="Real-time connection (chat, requests)"
          status={socketStatus}
          loading={socketLoading}
          onCheck={checkSocket}
          buttonLabel="Check Socket.IO"
          extra={
            socketStatus &&
            !socketLoading && (
              <p className="mt-3 text-sm text-gray-600 dark:text-light-400">
                {socketStatus.message}
              </p>
            )
          }
        />

        {/* Discord webhook */}
        <StatusCard
          icon={Send}
          title="Discord webhook"
          subtitle="Sends a test message to every Discord webhook (task board, employee requests)"
          status={discordStatus}
          loading={discordLoading}
          onCheck={sendDiscordTest}
          buttonLabel="Send test to all webhooks"
          extra={
            discordStatus &&
            !discordLoading && (
              <div className="mt-3 space-y-1.5 text-sm break-words">
                <p className={discordStatus.ok ? "text-muted" : "text-danger"}>{discordStatus.message}</p>
                {discordStatus.results?.length > 0 && (
                  <ul className="space-y-1">
                    {discordStatus.results.map((r) => (
                      <li key={r.key} className={r.ok ? "text-ok" : "text-danger"}>
                        {r.ok ? "✓" : "✗"} <strong>{r.label}</strong>
                        {!r.ok && <span className="block text-xs opacity-80">{r.message}</span>}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )
          }
        />

        {/* Firebase: test notification (to me) */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          className="card flex flex-col md:col-span-2"
        >
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-gray-100 dark:bg-dark-300 flex items-center justify-center">
                <Bell className="w-6 h-6 text-primary-accent" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-light-100">
                  Firebase notification test
                </h3>
                <p className="text-sm text-gray-500 dark:text-light-500">
                  Send a test push notification to yourself to verify FCM is
                  working.
                </p>
              </div>
            </div>
            <div className="shrink-0">
              {firebaseResult &&
                !firebaseLoading &&
                (firebaseResult.ok ? (
                  <CheckCircle2 className="w-6 h-6 text-green-500" />
                ) : (
                  <XCircle className="w-6 h-6 text-red-500" />
                ))}
            </div>
          </div>
          {firebaseResult && !firebaseLoading && firebaseResult.message && (
            <p
              className={`mt-3 text-sm ${firebaseResult.ok ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"}`}
            >
              {firebaseResult.message}
            </p>
          )}
          <Button
            onClick={sendTestNotification}
            disabled={firebaseLoading}
            className="mt-4 self-start flex items-center gap-2"
          >
            {firebaseLoading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Sending…
              </>
            ) : (
              <>
                <Bell className="w-4 h-4" />
                Send test notification to me
              </>
            )}
          </Button>
        </motion.div>

        {/* Notify users: all or selected */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          className="card flex flex-col md:col-span-2"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-xl bg-gray-100 dark:bg-dark-300 flex items-center justify-center shrink-0">
              <Bell className="w-6 h-6 text-primary-accent" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-light-100">
                Send notification
              </h3>
              <p className="text-sm text-gray-500 dark:text-light-500">
                Send to all users or choose specific users. Type your message
                below.
              </p>
            </div>
          </div>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-light-300 mb-2">
                Send to
              </label>
              <div className="flex flex-wrap gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="broadcastTarget"
                    checked={broadcastTarget === "all"}
                    onChange={() => setBroadcastTarget("all")}
                    className="text-primary-accent focus:ring-primary-accent"
                  />
                  <span className="text-sm text-gray-700 dark:text-light-300">
                    All users
                  </span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="broadcastTarget"
                    checked={broadcastTarget === "selected"}
                    onChange={() => setBroadcastTarget("selected")}
                    className="text-primary-accent focus:ring-primary-accent"
                  />
                  <span className="text-sm text-gray-700 dark:text-light-300">
                    Selected users
                  </span>
                </label>
              </div>
            </div>

            {broadcastTarget === "selected" && (
              <div className="rounded-lg border border-gray-200 dark:border-dark-100 p-3 max-h-[200px] overflow-y-auto">
                {employeesLoading ? (
                  <p className="text-sm text-gray-500 dark:text-light-500 flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" /> Loading users…
                  </p>
                ) : employeesList.length === 0 ? (
                  <p className="text-sm text-gray-500 dark:text-light-500">
                    No users found.
                  </p>
                ) : (
                  <>
                    <div className="flex items-center gap-2 mb-2">
                      <button
                        type="button"
                        onClick={selectAllUsers}
                        className="text-xs font-medium text-primary-accent hover:underline"
                      >
                        Select all
                      </button>
                      <span className="text-gray-400">|</span>
                      <button
                        type="button"
                        onClick={clearSelection}
                        className="text-xs font-medium text-gray-500 hover:underline"
                      >
                        Clear
                      </button>
                      {selectedUserIds.length > 0 && (
                        <span className="text-xs text-gray-500 dark:text-light-500">
                          ({selectedUserIds.length} selected)
                        </span>
                      )}
                    </div>
                    <div className="space-y-1">
                      {employeesList.map((emp) => {
                        const id = emp._id?.toString() || emp.id;
                        const name =
                          emp.fullNameEn ||
                          emp.fullNameAr ||
                          emp.nickname ||
                          emp.email ||
                          id;
                        const checked = id
                          ? selectedUserIds.includes(String(id))
                          : false;
                        return (
                          <label
                            key={id || name}
                            className="flex items-center gap-2 py-1 cursor-pointer hover:bg-gray-50 dark:hover:bg-dark-300 rounded px-2 -mx-2"
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleUserSelection(id)}
                              className="rounded text-primary-accent focus:ring-primary-accent"
                            />
                            <span className="text-sm text-gray-800 dark:text-light-200 truncate">
                              {name}
                              {emp.nickname && emp.nickname !== name
                                ? ` (@${emp.nickname})`
                                : ""}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-light-300 mb-1">
                Title (optional)
              </label>
              <input
                type="text"
                value={broadcastTitle}
                onChange={(e) => setBroadcastTitle(e.target.value)}
                placeholder="e.g. Announcement"
                className="input-field w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-light-300 mb-1">
                Message <span className="text-red-500">*</span>
              </label>
              <textarea
                value={broadcastBody}
                onChange={(e) => setBroadcastBody(e.target.value)}
                placeholder="Your message..."
                rows={3}
                className="input-field w-full resize-y min-h-[80px]"
              />
            </div>
          </div>
          {broadcastResult && !broadcastLoading && (
            <p
              className={`mt-3 text-sm ${broadcastResult.ok ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"}`}
            >
              {broadcastResult.message}
              {broadcastResult.totalUsers != null &&
                ` (${broadcastResult.totalUsers} user${broadcastResult.totalUsers !== 1 ? "s" : ""})`}
            </p>
          )}
          <Button
            onClick={sendBroadcastNotification}
            disabled={
              broadcastLoading ||
              !broadcastBody.trim() ||
              (broadcastTarget === "selected" && selectedUserIds.length === 0)
            }
            className="mt-4 self-start flex items-center gap-2"
          >
            {broadcastLoading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Sending…
              </>
            ) : (
              <>
                <Bell className="w-4 h-4" />
                {broadcastTarget === "all"
                  ? "Send to all users"
                  : `Send to ${selectedUserIds.length} user${selectedUserIds.length !== 1 ? "s" : ""}`}
              </>
            )}
          </Button>
        </motion.div>
      </div>

      <Toast
        isVisible={toast.isVisible}
        message={toast.message}
        type={toast.type}
        onClose={hideToast}
      />
    </PageWrapper>
  );
};

export default SystemStatus;
